import { StoresModel } from '../../models/StoresModel'
import { useStoreProfiles } from '../../store/useStoreProfiles'
import { useMessages } from '../../store/useMessages'
import { MessagesModel } from '../../models/MessagesModel'
import { BalanceModel } from '../../models/BalanceModel'
import { useInventory } from '../../store/useInventory'
import { ExpensesModel } from '../../models/ExpensesModel'
import { useExpenses } from '../../store/useExpenses'
import { OrdersModel } from '../../models/OrdersModel'
import { useOrders } from '../../store/useOrders'
import { LastSyncModel } from '../../models/LastSyncModel'
import { InventoryTransactionsModel } from '../../models/InventoryTransactionsModel'
import { TimersModel } from '../../models/TimersModel'
import { useTimers } from '../../store/timers'
import { useAllTimers } from '../../store/useAllTimers'
import { UserModel } from '../../models/UserModel'
import { useUserStore } from '../../store/useUserStore'
import { TaskModel } from '../../models/TaskModel'
import { useTaskProfiles } from '../../store/useTaskProfiles'
import { TimesheetsModel } from '../../models/TimesheetsModel'
import { useTimesheets } from '../../store/timesheets'
import { timesheetSync } from '../../synchronizations/helpers/timesheetSync'
import { CycleModel } from '../../models/CycleModel'
import { PayrollModel } from '../../models/PayrollModel'
import { useCurrentCycle } from '../../store/useCurrentCycle'
import { RecapResponsesModel } from '../../models/RecapReponsesModel'
import { useRecapStore } from '../../store/useRecapStore'
import { RecapDefinitionsModel } from '../../models/RecapDefinitionsModel'
import { AllPartsModel } from '../../models/AllParts'
import { useCodes } from '../../store/useCodes'
import { CodesModel } from '../../models/CodesModel'
import { PDFsModel } from '../../models/PDFsModel'
import { useDevicePDFsStore } from '../../store/useDevicePDFsStore'
import { usePayrollWindow } from '../../store/usePayrollWindow'
import { storeToRefs } from 'pinia'
import { captureException } from '@sentry/vue'
import { RecapImagesModel } from '../../models/RecapImagesModel'
import { StoreRecapDefinitionsModel } from '../../models/StoreRecapDefinitionsModel'
import { StoreRecapResponsesModel } from '../../models/StoreRecapResponsesModel'
import { StoreRecapImagesModel } from '../../models/StoreRecapImagesModel'

const loadUserData = async () => {
  const { getUserData } = UserModel()
  const { setUserData } = useUserStore()

  await getUserData().then((userData) => {
    if (userData) {
      setUserData(userData)
    }
  })

  await LastSyncModel().lastSyncDate()
}
const loadTimers = async () => {
  const { getTimers, getAllPreviousTimers } = TimersModel()
  const { setTimers } = useTimers()
  const { getAllParentTimers, getActiveChildTimer } = storeToRefs(useTimers())

  //set todays timer to the store
  await getTimers()
    .then(async (timers) => {
      if (timers) {
        const { loadTimesheets } = TimesheetsModel()
        const timesheets = await loadTimesheets()
        const { timesheetForDay } = timesheetSync()
        let unlock = true
        if (timesheets !== null || typeof timesheets !== 'undefined') {
          if (timesheetForDay(new Date(), timesheets)) {
            unlock = false
          }
        }

        await setTimers(timers, false, unlock, false)
        if (getAllParentTimers.value.length !== timers.length) {
          console.error('error setting parent timers on boot')
          console.error({ timersFromSync: timers })
          console.error({ getAllParentTimers: getAllParentTimers.value })
          captureException('error setting parent timers on boot')
        }
      }
    })
    .catch((error) => {
      console.log(`Error loading timers from database: ${error}`)
    })

  //set all timers on device for form optimization
  //set todays timer to the store
  const { setPreviousDaysTimers } = useAllTimers()
  await getAllPreviousTimers()
    .then((timers) => {
      if (timers) {
        setPreviousDaysTimers(timers)
      }
    })
    .catch((error) => {
      console.log(`Error loading previous timers from database: ${error}`)
    })

  //now, if there are any active task timers, load relevant recap definition
  if (getActiveChildTimer.value) {
    await useRecapStore().setRecapFromDatabase(
      getActiveChildTimer.value.data.recap_id,
      getActiveChildTimer.value.data.task_id
    )
  }
}

const loadTimesheets = async () => {
  const { loadTimesheets } = TimesheetsModel()
  const { setTimesheets, incompleteCheck } = useTimesheets()
  console.log('load timesheets')
  const timesheets = await loadTimesheets()
  if (timesheets !== null || typeof timesheets !== 'undefined') {
    setTimesheets(timesheets)
    //flag ts just in case
    for (const ts of timesheets) {
      await TimersModel().flagTimecardGenerated(ts.date.replaceAll(' 00:00:00', ''))
    }
  }
  //create 'incomplete' timesheets, if any
  console.log('incomplete check')
  incompleteCheck()
}

const loadCurrentCycle = async () => {
  const { loadCycleData } = CycleModel()
  const { setCycle } = useCurrentCycle()

  await loadCycleData().then((cycle) => {
    if (cycle !== null || typeof cycle !== 'undefined') {
      const { begin_date, end_date, ...rest } = cycle
      const newData = {
        begin_date: new Date(begin_date),
        end_date: new Date(end_date),
        ...rest,
      }
      setCycle(newData, { write: false })
    }
  })
}

const loadCurrentPayrollWindow = async () => {
  const { load } = PayrollModel()
  const { setPayroll } = usePayrollWindow()

  await load().then(async (prWindow) => {
    if (prWindow) {
      const { start, end, approval_cutoff_date, last_start, last_end, last_approval_cutoff_date } =
        prWindow
      const newData = {
        start: new Date(start),
        end: new Date(end),
        approval_cutoff_date: new Date(approval_cutoff_date),
        last_start: new Date(last_start),
        last_end: new Date(last_end),
        last_approval_cutoff_date: new Date(last_approval_cutoff_date),
      }
      await setPayroll(newData, { write: false })
    }
  })
}

const loadStores = async () => {
  const { getStores } = StoresModel()
  const storesOnDevice = await getStores()
  if (typeof storesOnDevice !== 'undefined' && storesOnDevice.length > 0) {
    useStoreProfiles().setStores(storesOnDevice, { writeStores: false })
  }
}
const loadTasks = async () => {
  const { getTasks } = TaskModel()
  const tasksOnDevice = await getTasks()
  if (tasksOnDevice) {
    await useTaskProfiles().setTasks(tasksOnDevice, { writeTasks: false })
  }
}
// const loadRecapDefinitions = async () => {
//   const { getAllRecapDefinitions } = RecapDefinitionsModel()
//   const { language } = useUserStore()
//   const definitions = await getAllRecapDefinitions()
//   if (language && definitions && definitions.length) {
//     useRecapStore().setRecapsFromDatabase(definitions, language)
//   }
// }
// const loadRecapResponses = async () => {
//   const { getAllRecapResponses } = RecapResponsesModel()
//   const responses = await getAllRecapResponses()
//   if (responses && responses.length) {
//     useRecapStore().setResponses(responses, { write: false })
//   }
// }
const loadMessages = async () => {
  const { getAllMessages } = MessagesModel()
  await getAllMessages().then(async (messages) => {
    await useMessages().setMessages(messages)
  })
}
const loadExpenses = async () => {
  const { getAll: getAllExpenses } = ExpensesModel()
  const { saveExpenses } = useExpenses()
  await getAllExpenses()
    .catch((error) => {
      console.log(`Error loading expenses from database: ${error}`)
      saveExpenses([])
    })
    .then((expenses) => {
      if (expenses) {
        if (expenses.length > 0) {
          saveExpenses(expenses, { write: false })
        } else {
          saveExpenses([], { write: false })
        }
      }
    })
}
const loadBalances = async () => {
  const { getAll: getAllBalances } = BalanceModel()
  const { setBalances } = useInventory()
  await getAllBalances()
    .catch((error) => {
      console.log(`Error loading balances from database: ${error}`)
      setBalances({})
    })
    .then((balances) => {
      if (balances) {
        if (balances) {
          setBalances(balances, { write: false })
        } else {
          setBalances({}, { write: false })
        }
      }
    })
}
const loadAllParts = async () => {
  const { getAll } = AllPartsModel()
  const { setAllParts } = useInventory()
  await getAll()
    .catch((error) => {
      console.log(`Error loading balances from database: ${error}`)
      setAllParts([])
    })
    .then((parts) => {
      if (parts) {
        if (parts.length > 0) {
          setAllParts(parts, { write: false })
        } else {
          setAllParts([], { write: false })
        }
      }
    })
}
const loadOrders = async () => {
  const { getAll: getAllOrders } = OrdersModel()
  const { setOrders } = useOrders()
  await getAllOrders()
    .catch((error) => {
      console.log(`Error loading orders from database: ${error}`)
      setOrders([])
    })
    .then((orders) => {
      if (orders) {
        if (orders.length > 0) {
          setOrders(orders)
        } else {
          setOrders([])
        }
      }
    })
}
const loadCodes = async () => {
  const { loadCodes } = CodesModel()
  const { setCodes } = useCodes()
  await loadCodes().then((codes) => {
    if (codes) {
      setCodes(codes, true)
    }
  })
}

const loadPDFSonDevice = async () => {
  const { getSystemPDFs } = PDFsModel()
  const { setToPDFsStore } = useDevicePDFsStore()

  await getSystemPDFs().then((pdfs) => {
    setToPDFsStore(pdfs)
  })
}
export const loadQueue = [
  loadUserData,
  loadTimers,
  loadTimesheets,
  loadCurrentCycle,
  loadCurrentPayrollWindow,
  loadStores,
  loadTasks,
  // loadRecapDefinitions,
  // loadRecapResponses,
  loadMessages,
  loadExpenses,
  loadBalances,
  loadAllParts,
  // loadOrders,
  loadCodes,
  loadPDFSonDevice,
]

const clearUser = async () => {
  const { clearUsersData } = UserModel()
  await clearUsersData()
}
const clearTimers = async () => {
  const { clearTimers } = TimersModel()
  await clearTimers()
}
const clearTimesheets = async () => {
  const { clearTimesheets } = TimesheetsModel()
  await clearTimesheets()
}
const clearCurrentCycle = async () => {
  const { clearCycle } = CycleModel()
  await clearCycle()
}
const clearCurrentPayrollWindow = async () => {
  const { clear } = PayrollModel()
  await clear()
}
const clearTransactions = async () => {
  const { clearTransactions } = InventoryTransactionsModel()
  await clearTransactions()
}
const clearStores = async () => {
  const { clearStores } = StoresModel()
  await clearStores()
}
const clearTasks = async () => {
  const { clearTasks } = TaskModel()
  await clearTasks()
}
const clearRecaps = async () => {
  const { clearRecapDefinitions } = RecapDefinitionsModel()
  await clearRecapDefinitions()
}
const clearRecapResponses = async () => {
  const { clearRecapResponses } = RecapResponsesModel()
  await clearRecapResponses()
}
const clearRecapImages = async () => {
  const { clearRecapImages } = RecapImagesModel()
  await clearRecapImages()
}
const clearStoreRecaps = async () => {
  const { clearStoreRecaps } = StoreRecapDefinitionsModel()
  await clearStoreRecaps()
}
const clearStoreRecapResponses = async () => {
  const { clearStoreRecapResponses } = StoreRecapResponsesModel()
  await clearStoreRecapResponses()
}
const clearStoreRecapImages = async () => {
  const { clearStoreRecapImages } = StoreRecapImagesModel()
  await clearStoreRecapImages()
}
const clearMessages = async () => {
  const { clearMessages } = MessagesModel()
  await clearMessages()
}
const clearExpenses = async () => {
  const { clearExpenses } = ExpensesModel()
  await clearExpenses()
}
const clearBalances = async () => {
  const { clearBalances } = BalanceModel()
  await clearBalances()
}
const clearAllParts = async () => {
  const { clearParts } = AllPartsModel()
  await clearParts()
}
const clearOrders = async () => {
  const { clearOrders } = OrdersModel()
  await clearOrders()
}
const clearLastSyncDates = async () => {
  const { clearSyncDates } = LastSyncModel()
  await clearSyncDates()
}
const clearCodes = async () => {
  const { clearCodes } = CodesModel()
  await clearCodes()
}
const clearPdfs = async () => {
  const { clearPDFs } = PDFsModel()
  await clearPDFs()
}
export const clearQueue = [
  clearUser,
  clearTimers,
  clearTimesheets,
  clearCurrentCycle,
  clearCurrentPayrollWindow,
  clearTransactions,
  clearStores,
  clearTasks,
  clearRecaps,
  clearRecapResponses,
  clearRecapImages,
  clearStoreRecaps,
  clearStoreRecapResponses,
  clearStoreRecapImages,
  clearMessages,
  clearExpenses,
  clearBalances,
  clearAllParts,
  // clearOrders,
  clearLastSyncDates,
  clearCodes,
  clearPdfs,
]
